import React from 'react';
import { Box, Typography, List, ListItemButton } from '@mui/material';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import EmptyState from '../../../common/EmptyState';

/**
 * EmployeeSelectStep - Reusable employee selection step for wizard modals
 *
 * @param {Object} props
 * @param {Array} props.mandatoryAttendees - Array of mandatory attendees with employee info
 * @param {Function} props.onSelectEmployee - Callback when an employee is selected
 * @param {string} props.description - Description text shown above the list
 * @param {string} props.emptyStateDescription - Description for empty state when no attendees
 */
const EmployeeSelectStep = ({
  mandatoryAttendees = [],
  onSelectEmployee,
  description = 'Select an employee',
  emptyStateDescription = 'Add mandatory attendees to the meeting',
}) => {
  if (mandatoryAttendees.length === 0) {
    return (
      <EmptyState
        title="No mandatory attendees"
        description={emptyStateDescription}
      />
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {description}
      </Typography>
      <List sx={{ p: 0 }}>
        {mandatoryAttendees.map((attendee) => {
          const employee = attendee.employee || attendee;
          return (
            <ListItemButton
              key={employee.id}
              onClick={() => onSelectEmployee(employee)}
              sx={{
                borderRadius: 1,
                mb: 1,
                border: '1px solid #E2E8F0',
                '&:hover': {
                  backgroundColor: 'rgba(37, 99, 235, 0.05)',
                  borderColor: '#2563EB',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <EmployeeAvatar employee={employee} size={40} />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {employee.fullName || 'Unknown Employee'}
                  </Typography>
                  {employee.email && (
                    <Typography variant="caption" color="text.secondary">
                      {employee.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default EmployeeSelectStep;
